import { getCurrentInstance, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

/**
 * A one-shot action handed to an app through its URL (I-2348). The shell's
 * command palette offers app actions („Neue Wiki-Seite“) before the app is
 * loaded; it navigates to the app with a query parameter, e.g.
 * `/wiki?new=page`, and the app's view runs the action here once.
 *
 * The parameter is removed from the URL (replace, no history entry) before
 * `run` is called, so a reload or the back button never repeats it. `with`
 * names companion parameters that belong to the same intent (`?compose=1&to=…`):
 * they are handed to `run` and removed along with it. With `ready`, the action
 * waits until the view can run it (data loaded, editor mounted). Call in
 * `setup()`.
 */
export function useRouteIntent(
  param: string,
  run: (value: string, companions: Record<string, string>) => void | Promise<void>,
  options: { ready?: () => boolean; with?: readonly string[] } = {}
): void {
  // Outside a router (a component test): nothing can arrive.
  if (!getCurrentInstance()?.appContext.config.globalProperties.$router) return;
  const route = useRoute();
  const router = useRouter();
  const companionNames = options.with ?? [];
  let busy = false;
  const first = (raw: unknown) => (Array.isArray(raw) ? raw[0] : raw);
  watch(
    () => [route.query[param], options.ready ? options.ready() : true] as const,
    async ([raw, ready]) => {
      const value = first(raw);
      if (typeof value !== 'string' || !value || !ready || busy) return;
      busy = true;
      try {
        const query = { ...route.query };
        const companions: Record<string, string> = {};
        for (const name of companionNames) {
          const v = first(query[name]);
          if (typeof v === 'string' && v) companions[name] = v;
          delete query[name];
        }
        delete query[param];
        await router.replace({ path: route.path, query, hash: route.hash }).catch(() => undefined);
        await run(value, companions);
      } finally {
        busy = false;
      }
    },
    { immediate: true }
  );
}
